import { fromBase64Url, randomBytes, sha256, toBase64Url, toCrockford, utf8 } from './encoding.ts';
import { VaultError, type MasterKey } from './vault.ts';

/**
 * An account's own key pairs, for sharing realms (docs/SHARING-PLAN.md section 3.2).
 *
 * - **Exchange** (ECDH P-256): others wrap a realm's edition key to it.
 * - **Signing** (ECDSA P-256): what the account publishes (memberships, editions) is signed.
 *
 * The public halves go to the server as raw uncompressed points (65 bytes, base64url).
 * The private halves are sealed under a key derived from the master key, so every device
 * the account unlocks has them and the server never does.
 *
 * Sealed format: base64url of version(1) | iv(12) | AES-256-GCM of the JSON
 * `{"exchange": pkcs8, "signing": pkcs8}` (each base64url), with the user id as
 * authenticated data, so one account's sealed keys can't be passed off as another's.
 */

const SEAL_VERSION = 1;
const FINGERPRINT_FORMAT = 'realmery-account-key-1';
const EXCHANGE = { name: 'ECDH', namedCurve: 'P-256' } as const;
const SIGNING = { name: 'ECDSA', namedCurve: 'P-256' } as const;

/** What the server keeps: the public halves, and the private halves sealed. */
export interface AccountKeyRecord {
  readonly exchangePublic: string;
  readonly signingPublic: string;
  readonly sealedPrivate: string;
}

/** The account's keys, opened: private keys are non-extractable. */
export interface AccountKeys {
  readonly record: AccountKeyRecord;
  readonly exchange: CryptoKeyPair;
  readonly signing: CryptoKeyPair;
  readonly fingerprint: string;
}

function sealData(userId: string): Uint8Array<ArrayBuffer> {
  return utf8(`${FINGERPRINT_FORMAT}\0sealed\0${userId}`);
}

/** Makes the account's key pairs and seals the private halves for the server. */
export async function generateAccountKeys(master: MasterKey, userId: string): Promise<AccountKeys> {
  const exchange = (await crypto.subtle.generateKey(EXCHANGE, true, ['deriveKey', 'deriveBits'])) as CryptoKeyPair;
  const signing = (await crypto.subtle.generateKey(SIGNING, true, ['sign', 'verify'])) as CryptoKeyPair;
  const privateJson = JSON.stringify({
    exchange: toBase64Url(new Uint8Array((await crypto.subtle.exportKey('pkcs8', exchange.privateKey)) as ArrayBuffer)),
    signing: toBase64Url(new Uint8Array((await crypto.subtle.exportKey('pkcs8', signing.privateKey)) as ArrayBuffer)),
  });
  const iv = randomBytes(12);
  const sealed = new Uint8Array(
    await crypto.subtle.encrypt({ name: 'AES-GCM', iv, additionalData: sealData(userId) }, await master.deriveKey('identity'), utf8(privateJson)),
  );
  const out = new Uint8Array(1 + iv.length + sealed.length);
  out[0] = SEAL_VERSION;
  out.set(iv, 1);
  out.set(sealed, 1 + iv.length);
  const record: AccountKeyRecord = {
    exchangePublic: toBase64Url(new Uint8Array((await crypto.subtle.exportKey('raw', exchange.publicKey)) as ArrayBuffer)),
    signingPublic: toBase64Url(new Uint8Array((await crypto.subtle.exportKey('raw', signing.publicKey)) as ArrayBuffer)),
    sealedPrivate: toBase64Url(out),
  };
  // Opened again, so the private keys held from here on are non-extractable, as after any unlock.
  return openAccountKeys(master, userId, record);
}

/**
 * Opens the account's sealed keys, and checks the public halves the server gave back are
 * the ones that belong to them: a server that swapped in its own public key is caught here.
 */
export async function openAccountKeys(master: MasterKey, userId: string, record: AccountKeyRecord): Promise<AccountKeys> {
  let parsed: { exchange: string; signing: string };
  try {
    const bytes = fromBase64Url(record.sealedPrivate);
    if (bytes[0] !== SEAL_VERSION || bytes.length < 1 + 12 + 16) throw new Error('format');
    const plain = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: bytes.slice(1, 13), additionalData: sealData(userId) },
      await master.deriveKey('identity'),
      bytes.slice(13),
    );
    parsed = JSON.parse(new TextDecoder().decode(plain)) as { exchange: string; signing: string };
  } catch {
    throw new VaultError('malformed', "This account's sharing keys can't be opened: they're damaged, or not this account's.");
  }
  const exchange = await importPair(parsed.exchange, record.exchangePublic, EXCHANGE, ['deriveKey', 'deriveBits'], []);
  const signing = await importPair(parsed.signing, record.signingPublic, SIGNING, ['sign'], ['verify']);
  return { record, exchange, signing, fingerprint: await accountFingerprint(userId, record) };
}

async function importPair(
  pkcs8: string,
  publicRaw: string,
  algorithm: typeof EXCHANGE | typeof SIGNING,
  privateUses: Parameters<typeof crypto.subtle.importKey>[4],
  publicUses: Parameters<typeof crypto.subtle.importKey>[4],
): Promise<CryptoKeyPair> {
  // A JWK of the private key carries its public point (x, y): it must match the record's.
  const extractable = await crypto.subtle.importKey('pkcs8', fromBase64Url(pkcs8), algorithm, true, privateUses);
  const jwk = (await crypto.subtle.exportKey('jwk', extractable)) as JsonWebKey;
  const raw = fromBase64Url(publicRaw);
  const point = new Uint8Array(65);
  point[0] = 4;
  point.set(fromBase64Url(jwk.x!), 1);
  point.set(fromBase64Url(jwk.y!), 33);
  if (raw.length !== 65 || raw.some((b, i) => b !== point[i])) {
    throw new VaultError('malformed', "This account's public sharing key doesn't match its private key.");
  }
  const privateKey = await crypto.subtle.importKey('pkcs8', fromBase64Url(pkcs8), algorithm, false, privateUses);
  const publicKey = await crypto.subtle.importKey('raw', raw, algorithm, true, publicUses);
  return { privateKey, publicKey };
}

/**
 * The account's fingerprint, to compare once with someone you share with: 100 bits of
 * SHA-256 over the account id and both public keys, as 20 Crockford base32 characters in
 * groups of four, like a recovery key (`7KQ2-M9XA-…`).
 */
export async function accountFingerprint(userId: string, record: Pick<AccountKeyRecord, 'exchangePublic' | 'signingPublic'>): Promise<string> {
  const digest = await sha256(utf8(`${FINGERPRINT_FORMAT}\0${userId}\0${record.exchangePublic}\0${record.signingPublic}`));
  // 13 bytes is 104 bits; 20 characters of 5 bits each take the first 100.
  return toCrockford(digest.slice(0, 13)).slice(0, 20).match(/.{4}/g)!.join('-');
}

/** Whether a public key is a well-formed uncompressed P-256 point, as the server checks too. */
export function isPublicPoint(text: string): boolean {
  if (!/^[A-Za-z0-9_-]{87}$/.test(text)) return false;
  try {
    const raw = fromBase64Url(text);
    return raw.length === 65 && raw[0] === 4;
  } catch {
    return false;
  }
}
